import {
  DeviceWithInstalledPower,
  ForecastVisualizationControl,
  OutagesDeviceList,
} from '@e2m-customer-portal/domains';
import { MarketLocation, useQueryParams } from '@e2m-customer-portal/hooks';
import { ErrorComponent, Loading, PageTitle } from '@e2m-customer-portal/ui';
import { addDays, Interval, startOfToday, startOfWeek } from 'date-fns';
import * as R from 'ramda';
import { ReactElement, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

export interface OutagesProps {
  marketLocation: MarketLocation;
}

export function Outages({ marketLocation }: Readonly<OutagesProps>): ReactElement<OutagesProps> {
  const [t] = useTranslation();

  const queryParams = useQueryParams();
  const allDevices: Array<DeviceWithInstalledPower> = marketLocation.machines.flatMap((machine) =>
    machine.devices.map((device) => ({ ...device, installedPower: machine.installedPower })),
  );
  const selectedIds = JSON.parse(
    localStorage.getItem(`device-selection-${marketLocation.id}`) ??
      JSON.stringify(queryParams.has('selected') ? queryParams.getAll('selected') : R.pluck('id', allDevices)),
  ) as Array<string>;
  const [devices, setDevices] = useState(
    allDevices.length > 1 && allDevices.length !== selectedIds.length
      ? allDevices.filter((device) => selectedIds.includes(device.id))
      : allDevices,
  );

  useEffect(() => {
    if (allDevices.length > 1 && allDevices.length !== devices.length)
      localStorage.setItem(`device-selection-${marketLocation.id}`, JSON.stringify(devices.map((device) => device.id)));
    else localStorage.removeItem(`device-selection-${marketLocation.id}`);
  }, [marketLocation.id, devices]);

  const preselectedInterval: Interval = {
    start: startOfWeek(startOfToday(), { weekStartsOn: 1 }),
    end: addDays(startOfWeek(startOfToday(), { weekStartsOn: 1 }), 27),
  };
  const [interval, setInterval] = useState(preselectedInterval);

  return (
    <>
      <PageTitle
        label={
          marketLocation.externalName && marketLocation.externalName !== marketLocation.name
            ? marketLocation.name
            : t('MASTER_DATA_TAB_PLANT')
        }
        title={marketLocation.externalName || marketLocation.name}
      />
      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <ForecastVisualizationControl
          marketLocation={marketLocation}
          availableDevices={allDevices}
          devices={devices}
          onDevicesChange={(newDevices: DeviceWithInstalledPower[]) => {
            if (newDevices.length) setDevices(newDevices);
          }}
          date={new Date(interval.start)}
          days={28}
          onDateChange={(date: Date) => {
            setInterval(() => ({
              start: date,
              end: addDays(date, 27),
            }));
          }}
        />
        <Suspense fallback={<Loading />}>
          <OutagesDeviceList
            devices={allDevices}
            selectedDevices={devices}
            interval={interval}
            editable={marketLocation.permissions.editForecast}
          />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
